import { lazy } from 'react';
import {
  ListAlt,
  Person,
  PersonAdd,
  People,
  AnnouncementOutlined,
  AssignmentLate,
  Money,
  ViewModule,
  Apartment,
  AccountBalanceWallet,
  CompareArrows,
  AccountBalance,
  SupervisorAccount,
  Settings,
  LocalMallOutlined,
  LocalMall,
  CompareArrowsRounded,
  ListAltRounded,
  PlaylistAddCheckOutlined,
  PhoneIphone,
} from '@material-ui/icons';

import { ROLES } from '../constants/role';
import MurabahaRoutes from '../pages/Murabaha/configs/murabaha.route';
import MarketRoutes from '../pages/Murabaha/configs/market.route';

import { useSideBarIcons } from '../components/Sidebar/hooks/useSideBarIcons';
import AuthRoutes from '../auth/configs/auth.route';

// Pages
const Dividend = lazy(() => import('../pages/Dividend'));
const Users = lazy(() => import('../pages/Users'));
const Property = lazy(() => import('../pages/Property'));
const Profile = lazy(() => import('../pages/Profile'));
const Announcement = lazy(() => import('../pages/Announcement'));
const Trades = lazy(() => import('../pages/Trades'));
const Transfers = lazy(() => import('../pages/Transfers'));
const Orders = lazy(() => import('../pages/Orders'));
const InvestorMarket = lazy(() => import('../pages/InvestorMarket'));
const MarketPropertyListing = lazy(() =>
  import('../pages/InvestorMarket/MarketPropertyListing')
);
const PropertyPage = lazy(() => import('../pages/InvestorMarket/PropertyPage'));
const Holdings = lazy(() => import('../pages/Holdings'));
const Accounts = lazy(() => import('../pages/Account'));
const InvestorAccount = lazy(() => import('../pages/Account/Investor'));
const InvestorAccountDetailsView = lazy(() =>
  import('../pages/Account/Investor/InvestorAccountDetailsView')
);
const FeeGroups = lazy(() => import('../pages/FeeGroups'));
const FeeGroupUsersListing = lazy(() =>
  import('../pages/FeeGroups/FeeGroupUsersListing')
);
const AppConfig = lazy(() => import('../pages/AppConfig'));
const MurabahaInvestorUsers = lazy(() =>
  import('../pages/Murabaha/MurabahaInvestorUsers')
);
const MurabahaInvestorDetails = lazy(() =>
  import('../pages/Murabaha/MurabahaInvestorUsers/MurabahaInvestorDetails')
);

const Murabaha = lazy(() => import('../pages/Murabaha'));
const MurabahaBuyOrderDetails = lazy(() =>
  import('../pages/Murabaha/Buy/MurabahaBuyOrderDetails')
);

const TransferUnitsView = lazy(() => import('../pages/Murabaha/TransferUnits'));
const MarketView = lazy(() => import('../pages/Market/MarketView'));
const MarketTradeView = lazy(() => import('../pages/Market/MarketTradeView'));
const BankIntegration = lazy(() => import('../pages/BankIntegration'));
const CreateApiClientView = lazy(() =>
  import('../pages/BankIntegration/CreateApiClientView')
);

const ApiClientDetailsView = lazy(() =>
  import('../pages/BankIntegration/ApiClientDetailsView')
);
const ApiDocsView = lazy(() => import('../pages/ApiDocs'));

const MobileApp = lazy(() => import('../pages/MobileApp'));

const {
  MenuIcon,
  AnouncementsIcon,
  BankIcon,
  DividendIcon,
  HoldingsIcon,
  MobileIcon,
  OrdersIcon,
  PropertiesIcon,
  SettingsIcon,
  TradeIcon,
  TransfersIcon,
  UsersIcon,
  WalletIcon,
  FeesIcon,
  UsersListIcon,
  AddUserIcon,
  MarketIcon,
  MoneyWalletIcon,
  WalletEditIcon,
  MarketAncIcon,
  PropertyAncIcon,
  //
  BuyIcon,
  TransferIcon,
  TasksIcon,
  ListIcon,
} = useSideBarIcons();

export const protectedRoutes = [
  // ---------------- New Market Route ------------------ //
  {
    path: '/market-legacy/:id',
    component: PropertyPage,
    exact: true,
    hiddenInSidebar: true,
    roles: [ROLES.INVESTOR, ROLES.MANAGER, ROLES.ADMIN],
  },
  {
    path: MarketRoutes.MARKET,
    component: MarketView,
    exact: true,
    hiddenInSidebar: true,
    roles: [
      ROLES.INVESTOR,
      ROLES.MANAGER,
      ROLES.ADMIN,
      ROLES.MURABAHA_REPRESENTATIVE,
    ],
  },

  {
    path: MarketRoutes.MARKET_TRADE,
    component: MarketTradeView,
    exact: true,
    hiddenInSidebar: true,
    roles: [
      ROLES.INVESTOR,
      ROLES.MANAGER,
      ROLES.ADMIN,
      ROLES.MURABAHA_REPRESENTATIVE,
    ],
  },

  // --------------------------------------------------- //

  {
    name: 'market',
    path: '/market',
    component: MarketPropertyListing,
    // component: InvestorMarket,
    icon: ListAlt,
    newIcon: MarketIcon,
    roles: [ROLES.INVESTOR, ROLES.MANAGER, ROLES.ADMIN],
  },
  {
    path: '/watchlist/:id',
    component: PropertyPage,
    exact: true,
    hiddenInSidebar: true,
  },
  {
    name: 'users',
    path: '/users',
    component: Users,
    icon: Person,
    newIcon: UsersListIcon,
    showAsDropDownInSidebar: true,
    roles: [ROLES.ADMIN],
    children: [
      {
        name: 'application_users',
        path: '/users/application',
        icon: People,
        newIcon: UsersIcon,
      },
      {
        name: 'invited_users',
        path: '/users/invited',
        icon: PersonAdd,
        newIcon: AddUserIcon,
      },
    ],
  },

  {
    path: AuthRoutes.INVITE_INVESTOR,
    exact: true,
    hiddenInSidebar: true,
    roles: [ROLES.ADMIN],
  },

  // ------------------------------ Murabaha Users Start ------------------------------ //

  // Clients
  {
    name: 'murabaha_clients',
    path: MurabahaRoutes.MURABAHA_USERS,
    component: MurabahaInvestorUsers,
    icon: Person,
    newIcon: UsersListIcon,
    showAsDropDownInSidebar: true,
    roles: [ROLES.MURABAHA_REPRESENTATIVE],
    children: [
      {
        name: 'create_new_clients',
        path: MurabahaRoutes.CREATE_MURABAHA_USER,
        icon: PersonAdd,
        newIcon: AddUserIcon,
      },

      {
        name: 'clients_list',
        path: MurabahaRoutes.MURABAHA_INVESTORS,
        icon: People,
        newIcon: UsersIcon,
      },
    ],
  },

  // Operations
  {
    name: 'murabaha_operations',
    path: MurabahaRoutes.MURABAHA,
    component: Murabaha,
    icon: LocalMallOutlined,
    newIcon: TransfersIcon,
    showAsDropDownInSidebar: true,
    roles: [ROLES.MURABAHA_REPRESENTATIVE],
    children: [
      {
        name: 'buy_units',
        path: MurabahaRoutes.MURABAHA_BUY,
        icon: LocalMall,
        newIcon: BuyIcon,
      },
      {
        name: 'transfer_to_clients',
        path: MurabahaRoutes.TRANSFER_UNITS,
        icon: CompareArrowsRounded,
        newIcon: TransferIcon,
      },

      {
        name: 'murabaha_tasks',
        path: MurabahaRoutes.MURABAHA_TASKS,
        icon: PlaylistAddCheckOutlined,
        newIcon: TasksIcon,
      },
      {
        name: 'unit_transfer_list',
        path: MurabahaRoutes.TRANSFER_UNITS_HISTORY,
        icon: ListAltRounded,
        newIcon: ListIcon,
      },
    ],
  },

  {
    path: MurabahaRoutes.MURABAHA_USER_DETAILS,
    component: MurabahaInvestorDetails,
    exact: true,
    hiddenInSidebar: true,
    roles: [ROLES.MURABAHA_REPRESENTATIVE],
  },

  // {
  //   name: 'murabaha_operations',
  //   path: MurabahaRoutes.MURABAHA,
  //   component: Murabaha,
  //   icon: LocalMallOutlined,
  //   showAsDropDownInSidebar: true,
  //   roles: [ROLES.MURABAHA_REPRESENTATIVE],
  //   children: [
  //     {
  //       name: 'murabaha_buy',
  //       path: MurabahaRoutes.MURABAHA_BUY,
  //       icon: LocalMall,
  //     },

  //     {
  //       name: 'murabaha_tasks',
  //       path: MurabahaRoutes.MURABAHA_TASKS,
  //       icon: PlaylistAddCheckOutlined,
  //     },
  //   ],
  // },

  {
    name: 'transfer_units',
    path: MurabahaRoutes.TRANSFER_UNITS_HOME,
    component: TransferUnitsView,
    icon: CompareArrowsRounded,
    showAsDropDownInSidebar: true,
    roles: [ROLES.MURABAHA_REPRESENTATIVE],
    hiddenInSidebar: true,
  },

  {
    path: MurabahaRoutes.TRANSFER_UNITS_DETAILS,
    exact: true,
    hiddenInSidebar: true,
    roles: [ROLES.MURABAHA_REPRESENTATIVE],
  },
  {
    path: MurabahaRoutes.TRANSFER_MARKET_SELL,
    exact: true,
    hiddenInSidebar: true,
    roles: [ROLES.MURABAHA_REPRESENTATIVE],
  },
  {
    path: MurabahaRoutes.TRANSFER_SELL_WITHDRAWAL,
    exact: true,
    hiddenInSidebar: true,
    roles: [ROLES.MURABAHA_REPRESENTATIVE],
  },

  {
    path: MurabahaRoutes.MURABAHA_BUY_ORDER_DETAILS,
    component: MurabahaBuyOrderDetails,
    exact: true,
    hiddenInSidebar: true,
    roles: [ROLES.MURABAHA_REPRESENTATIVE],
  },

  // ------------------------------ Murabaha Users End ------------------------------ //
  {
    name: 'properties',
    path: '/property',
    component: Property,
    icon: ListAlt,
    newIcon: PropertiesIcon,
    roles: [ROLES.MANAGER, ROLES.ADMIN],
  },
  {
    name: 'orders',
    path: '/orders',
    component: Orders,
    icon: ViewModule,
    newIcon: OrdersIcon,
    roles: [ROLES.INVESTOR, ROLES.ADMIN],
  },
  {
    name: 'trades',
    path: '/trades',
    component: Trades,
    icon: ListAlt,
    newIcon: TradeIcon,
    roles: [ROLES.ADMIN],
  },
  {
    name: 'unit_transfers',
    path: '/unit-transfers',
    component: Transfers,
    icon: CompareArrows,
    newIcon: TransfersIcon,
    roles: [ROLES.ADMIN],
  },
  {
    name: 'holdings',
    path: '/holdings',
    component: Holdings,
    icon: Apartment,
    newIcon: HoldingsIcon,
    roles: [ROLES.INVESTOR],
  },
  {
    name: 'dividends',
    path: '/dividend',
    component: Dividend,
    icon: AccountBalanceWallet,
    newIcon: DividendIcon,
    roles: [ROLES.MANAGER, ROLES.ADMIN],
  },
  {
    name: 'users_accounts',
    path: '/accounts/investor',
    component: InvestorAccountDetailsView,
    // component: InvestorAccount,
    icon: AccountBalance,
    newIcon: WalletIcon,
    roles: [ROLES.INVESTOR],
    exact: true,
  },
  {
    name: 'users_accounts',
    path: '/accounts',
    component: Accounts,
    icon: AccountBalance,
    newIcon: WalletIcon,
    roles: [ROLES.ADMIN],
    showAsDropDownInSidebar: true,
    children: [
      {
        name: 'management',
        path: '/accounts/management',
        icon: SupervisorAccount,
        newIcon: WalletEditIcon,
      },
      {
        name: 'ibdax_wallet',
        path: '/accounts/ibdax',
        icon: AccountBalanceWallet,
        newIcon: MoneyWalletIcon,
      },
    ],
  },
  {
    name: 'fee_groups',
    path: '/fee-groups',
    component: FeeGroups,
    icon: Money,
    newIcon: FeesIcon,
    roles: [ROLES.ADMIN],
    exact: true,
  },
  {
    name: 'announcements',
    path: '/announcements',
    component: Announcement,
    icon: AnnouncementOutlined,
    newIcon: AnouncementsIcon,
    showAsDropDownInSidebar: true,
    roles: [ROLES.INVESTOR, ROLES.MANAGER, ROLES.ADMIN],
    children: [
      {
        name: 'property_announcements',
        path: '/announcements/property',
        icon: AssignmentLate,
        newIcon: PropertyAncIcon,
      },
      {
        name: 'market_announcements',
        path: '/announcements/market',
        icon: AnnouncementOutlined,
        newIcon: MarketAncIcon,
      },
    ],
  },
  {
    path: '/fee-group-users/:id',
    component: FeeGroupUsersListing,
    exact: true,
    hiddenInSidebar: true,
  },
  {
    path: '/profile',
    component: Profile,
    exact: true,
    hiddenInSidebar: true,
    hideRoleSelectorRX: /^\/profile/,
    hideSidebarRX: /^\/profile/,
  },
  {
    name: 'app_config',
    path: '/app-config',
    component: AppConfig,
    icon: Settings,
    newIcon: SettingsIcon,
    roles: [ROLES.ADMIN],
    exact: true,
  },

  // -------- Bank Integration Routes --------- //
  {
    name: 'bank_integration',
    path: MurabahaRoutes.BANK_INTEGRATION,
    component: BankIntegration,
    icon: AccountBalance,
    newIcon: BankIcon,
    roles: [ROLES.ADMIN],
    exact: true,
  },

  {
    path: MurabahaRoutes.BANK_INTEGRATION_API_APP_CLIENTS,
    component: CreateApiClientView,
    exact: true,
    hiddenInSidebar: true,
    roles: [ROLES.ADMIN],
  },
  {
    path: MurabahaRoutes.BANK_INTEGRATION_API_APP_CLIENTS_SUCCESS,
    component: ApiClientDetailsView,
    exact: true,
    hiddenInSidebar: true,
    roles: [ROLES.ADMIN],
  },

  // ----------------------------------------- //

  // ------------ App Management Routes ----------- //
  {
    name: 'mobile_app',
    path: '/mobile-app',
    component: MobileApp,
    icon: PhoneIphone,
    newIcon: MobileIcon,
    roles: [ROLES.ADMIN],
    exact: true,
  },

  // --------------- API Docs ------------------ //
  {
    path: '/docs',
    component: ApiDocsView,
    hiddenInSidebar: true,
    exact: true,
  },

  // ----------------------------------------- //
];
